import React, { useEffect, useState, useRef } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  X,
  Globe,
  Star,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { adminApi, uploadToCloudinary, getVideoThumbnail } from '../../lib/api';
import { VideoThumbnailPicker } from './VideoThumbnailPicker';

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const generateCode = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
  return code;
};

const EventForm = ({ open, onOpenChange, event, onSaved }) => {
  const { toast } = useToast();
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoProgress, setPhotoProgress] = useState('');
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [thumbPickerIdx, setThumbPickerIdx] = useState(null);
  const [dragPhotoIdx, setDragPhotoIdx] = useState(null);
  const [dragVideoIdx, setDragVideoIdx] = useState(null);
  const heroInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const videosInputRef = useRef(null);
  const reelsInputRef = useRef(null);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    if (open) {
      adminApi.categories.list()
        .then((d) => setCategoryOptions(d || []))
        .catch(() => setCategoryOptions([]));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setData(
        event
          ? { ...event, photos: event.photos || [], videos: event.videos || [], embeds: event.embeds || [], categories: event.categories || [] }
          : {
              slug: '',
              title: '',
              event_type: '',
              event_date: '',
              location: '',
              description: '',
              hero_image: '',
              hero_public_id: '',
              hero_focal_x: 50,
              hero_focal_y: 50,
              access_code: generateCode(),
              photos: [],
              videos: [],
              embeds: [],
              categories: [],
              project_source: '',
              publishing_permission_confirmed: false,
              featured: false,
              published: true,
              order: 0,
            }
      );
    }
  }, [open, event]);

  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  const onTitleChange = (val) => {
    setData((prev) => ({
      ...prev,
      title: val,
      // Auto-generate slug only when creating
      slug: !event ? slugify(val) : prev.slug,
    }));
  };

  const handleHeroUpload = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      // Delete previous hero if any
      if (data.hero_public_id) {
        adminApi.deleteCloudinaryAsset(data.hero_public_id).catch(() => {});
      }
      const result = await uploadToCloudinary(file, 'maydenfilms/events');
      setData((prev) => ({ ...prev, hero_image: result.url, hero_public_id: result.public_id }));
      toast({ title: 'Hero photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    } finally {
      setUploadingHero(false);
    }
  };

  const handlePhotosUpload = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    setUploadingPhotos(true);
    setPhotoProgress(`0/${list.length}`);
    const uploaded = [];
    let done = 0;
    try {
      for (const file of list) {
        try {
          const result = await uploadToCloudinary(file, 'maydenfilms/events');
          uploaded.push({
            url: result.url,
            public_id: result.public_id,
            order: (data.photos?.length || 0) + uploaded.length,
          });
        } catch (e) {
          toast({ title: `Couldn't upload ${file.name}`, description: e.message, variant: 'destructive' });
        } finally {
          done += 1;
          setPhotoProgress(`${done}/${list.length}`);
        }
      }
      if (uploaded.length) {
        setData((prev) => ({ ...prev, photos: [...(prev.photos || []), ...uploaded] }));
        toast({ title: `${uploaded.length} of ${list.length} photo(s) uploaded` });
      }
    } finally {
      setUploadingPhotos(false);
      setPhotoProgress('');
    }
  };

  const removePhoto = (idx) => {
    const photo = data.photos[idx];
    if (photo?.public_id) {
      adminApi.deleteCloudinaryAsset(photo.public_id).catch(() => {});
    }
    setData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== idx),
    }));
  };

  const handleVideosUpload = async (files, orientation = 'horizontal') => {
    if (!files || files.length === 0) return;
    setUploadingVideos(true);
    const uploaded = [];
    try {
      for (const file of Array.from(files)) {
        try {
          const result = await uploadToCloudinary(file, 'maydenfilms/events', 'video');
          uploaded.push({
            url: result.url,
            public_id: result.public_id,
            thumbnail: '',
            title: file.name.replace(/\.[^/.]+$/, ''),
            order: (data.videos?.length || 0) + uploaded.length,
            orientation,
          });
        } catch (e) {
          toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
        }
      }
      setData((prev) => ({ ...prev, videos: [...(prev.videos || []), ...uploaded] }));
      if (uploaded.length) toast({ title: `${uploaded.length} ${orientation === 'vertical' ? 'reel' : 'video'}(s) uploaded` });
    } finally {
      setUploadingVideos(false);
    }
  };

  // YouTube / Vimeo URL embeds
  const addEmbed = () => setData((prev) => ({ ...prev, embeds: [...(prev.embeds || []), { url: '', title: '', order: (prev.embeds?.length || 0) }] }));
  const updateEmbed = (idx, key, value) => setData((prev) => ({ ...prev, embeds: prev.embeds.map((em, i) => (i === idx ? { ...em, [key]: value } : em)) }));
  const removeEmbed = (idx) => setData((prev) => ({ ...prev, embeds: prev.embeds.filter((_, i) => i !== idx) }));

  // Per-photo caption / alt text
  const updatePhotoMeta = (idx, key, value) => setData((prev) => ({ ...prev, photos: prev.photos.map((p, i) => (i === idx ? { ...p, [key]: value } : p)) }));

  const removeVideo = (idx) => {
    const video = data.videos[idx];
    if (video?.public_id) {
      adminApi.deleteCloudinaryAsset(video.public_id, 'video').catch(() => {});
    }
    setData((prev) => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== idx),
    }));
  };

  // Reorder a list (photos/videos) by drag-and-drop. Also rewrites `order` so
  // the public site renders the new sequence after the event is saved.
  const reorderList = (listKey, fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setData((prev) => {
      const list = [...(prev[listKey] || [])];
      const [moved] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, moved);
      const next = list.map((item, i) => ({ ...item, order: i }));
      return { ...prev, [listKey]: next };
    });
  };

  // Mark one video as the event's featured spotlight (un-features all others).
  const toggleVideoFeatured = (idx) => {
    setData((prev) => {
      const videos = [...(prev.videos || [])];
      const isCurrentlyFeatured = !!videos[idx]?.featured;
      const next = videos.map((v, i) => ({
        ...v,
        featured: i === idx ? !isCurrentlyFeatured : false,
      }));
      return { ...prev, videos: next };
    });
  };

  const toggleCategory = (cat) => {
    setData((prev) => {
      const cats = prev.categories || [];
      return {
        ...prev,
        categories: cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat],
      };
    });
  };

  const removeHero = () => {
    if (data.hero_public_id) {
      adminApi.deleteCloudinaryAsset(data.hero_public_id).catch(() => {});
    }
    setData((prev) => ({ ...prev, hero_image: '', hero_public_id: '' }));
  };

  const handleSave = async () => {
    if (!data.title || !data.slug) {
      toast({ title: 'Missing fields', description: 'Title and slug are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (data.published && data.project_source === 'Client Commission' && !data.publishing_permission_confirmed) {
        const ok = window.confirm('This is a Client Commission and publishing permission is NOT confirmed. It will be saved, but it will NOT appear publicly until you tick "Publishing permission confirmed". Continue?');
        if (!ok) { setSaving(false); return; }
      }
      if (event) {
        await adminApi.events.update(event.id, data);
        toast({ title: 'Event updated' });
      } else {
        await adminApi.events.create(data);
        toast({ title: 'Event created' });
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#EADBC8] text-2xl">
            {event ? 'Edit Event' : 'New Event'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Title */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">Event Title *</label>
            <Input
              value={data.title || ''}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="e.g. Sarah & James Wedding"
              className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
              data-testid="event-title-input"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">URL Slug *</label>
            <div className="flex gap-2">
              <span className="text-gray-500 text-sm flex items-center px-3 bg-[#1A1612] border border-[#C5A880]/20 rounded-md">
                /our-work/
              </span>
              <Input
                value={data.slug || ''}
                onChange={(e) => update('slug', slugify(e.target.value))}
                placeholder="sarah-james-wedding"
                className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] flex-1"
                data-testid="event-slug-input"
              />
            </div>
          </div>

          {/* Photos are shown publicly on the Recent Events portfolio (no access code needed). */}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">Event Type</label>
              <Input
                value={data.event_type || ''}
                onChange={(e) => update('event_type', e.target.value)}
                placeholder="Wedding, Corporate, Birthday..."
                className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
              />
            </div>
            <div>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">Event Date</label>
              <Input
                value={data.event_date || ''}
                onChange={(e) => update('event_date', e.target.value)}
                placeholder="March 15, 2025"
                className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
              />
            </div>
          </div>

          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">Location</label>
            <Input
              value={data.location || ''}
              onChange={(e) => update('location', e.target.value)}
              placeholder="Crown Melbourne"
              className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
            />
          </div>

          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">Description (optional)</label>
            <Textarea
              value={data.description || ''}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              placeholder="A short description shown on the gallery page"
              className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
            />
          </div>

          {/* Project source, featured & publishing permission */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">Project Source</label>
              <select
                value={data.project_source || ''}
                onChange={(e) => update('project_source', e.target.value)}
                className="w-full bg-[#1A1612] border border-[#C5A880]/20 text-[#EADBC8] rounded-md px-3 py-2 text-sm"
                data-testid="event-project-source"
              >
                <option value="">Not specified</option>
                <option value="Client Commission">Client Commission</option>
                <option value="Self-Directed Creative Project">Self-Directed Creative Project</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer bg-[#1A1612] border border-[#C5A880]/20 rounded-md px-3 py-2.5 w-full">
                <input type="checkbox" checked={!!data.featured} onChange={(e) => update('featured', e.target.checked)} className="w-4 h-4 accent-[#C5A880]" data-testid="event-featured" />
                <span className="text-[#EADBC8]/85 text-sm">Feature this on the portfolio</span>
              </label>
            </div>
          </div>

          {data.project_source === 'Client Commission' && (
            <div className={`rounded-lg p-3 border ${data.publishing_permission_confirmed ? 'border-[#C5A880]/25 bg-[#1A1612]' : 'border-amber-500/40 bg-amber-500/5'}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={!!data.publishing_permission_confirmed} onChange={(e) => update('publishing_permission_confirmed', e.target.checked)} className="mt-1 w-4 h-4 accent-[#C5A880]" data-testid="event-permission" />
                <span className="text-[#EADBC8]/85 text-sm">
                  <span className="font-medium text-[#EADBC8]">Publishing permission confirmed.</span> Client work with recognisable people will not appear publicly until this is ticked.
                </span>
              </label>
            </div>
          )}

          {/* Categories */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">Categories</label>
            <p className="text-[#EADBC8]/50 text-xs mb-2">
              Tag this event so visitors find it via the "View Our Work" buttons on service pages
            </p>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const selected = data.categories || [];
                const known = new Set(categoryOptions.map((c) => c.slug));
                // Show all defined categories, plus any legacy slug already on this event
                const opts = [
                  ...categoryOptions.map((c) => ({ value: c.slug, label: c.name + (c.active ? '' : ' (hidden)') })),
                  ...selected.filter((s) => !known.has(s)).map((s) => ({ value: s, label: s + ' (removed)' })),
                ];
                if (opts.length === 0) {
                  return <p className="text-[#EADBC8]/40 text-sm">No categories yet — add them in the Categories section.</p>;
                }
                return opts.map((opt) => {
                  const active = selected.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => toggleCategory(opt.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        active
                          ? 'bg-[#C5A880] text-[#1F1A17] border border-[#C5A880]'
                          : 'bg-[#1A1612] text-[#EADBC8]/70 border border-[#C5A880]/20 hover:border-[#C5A880]/60'
                      }`}
                      data-testid={`category-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Hero photo */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">Hero Photo</label>
            <p className="text-gray-500 text-xs mb-2">
              The cover image shown on the "Our Work" page (1 photo)
            </p>
            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleHeroUpload(e.target.files?.[0])}
            />
            {data.hero_image ? (
              <div>
                <div className="relative inline-block">
                  <img
                    src={data.hero_image}
                    alt="Hero"
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      const x = Math.round(((e.clientX - r.left) / r.width) * 100);
                      const y = Math.round(((e.clientY - r.top) / r.height) * 100);
                      setData((prev) => ({ ...prev, hero_focal_x: x, hero_focal_y: y }));
                    }}
                    className="h-40 w-auto rounded-lg border border-[#C5A880]/20 cursor-crosshair"
                    data-testid="hero-focal-image"
                  />
                  <span
                    className="absolute w-5 h-5 rounded-full border-2 border-[#C5A880] bg-[#C5A880]/30 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ left: `${data.hero_focal_x ?? 50}%`, top: `${data.hero_focal_y ?? 50}%` }}
                  />
                  <button
                    onClick={removeHero}
                    className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-[#EADBC8] hover:bg-red-600"
                    data-testid="remove-hero-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
                <p className="text-[#EADBC8]/50 text-xs mt-1.5">Click the photo to set the focal point for correct cropping on desktop &amp; mobile.</p>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => heroInputRef.current?.click()}
                disabled={uploadingHero}
                className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 w-full py-8"
                data-testid="upload-hero-btn"
              >
                <Upload size={18} className="mr-2" />
                {uploadingHero ? 'Uploading...' : 'Upload Hero Photo'}
              </Button>
            )}
          </div>

          {/* Gallery photos */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">
              Gallery Photos ({data.photos?.length || 0})
            </label>
            <p className="text-gray-500 text-xs mb-2">
              Photos are shown publicly in the event gallery
            </p>
            <input
              ref={photosInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => handlePhotosUpload(e.target.files)}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => photosInputRef.current?.click()}
              disabled={uploadingPhotos}
              className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 w-full mb-3"
              data-testid="upload-photos-btn"
            >
              <Upload size={16} className="mr-2" />
              {uploadingPhotos ? `Uploading ${photoProgress}...` : 'Add Photos'}
            </Button>

            {data.photos?.length > 0 && (
              <>
                <p className="text-[#EADBC8]/50 text-xs mb-2">
                  Drag to reorder · oldest first appears on the public site
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 max-h-96 overflow-y-auto p-1">
                  {data.photos.map((photo, idx) => (
                    <div
                      key={photo.public_id || idx}
                      className={`group transition-opacity ${dragPhotoIdx === idx ? 'opacity-30' : 'opacity-100'}`}
                      data-testid={`event-photo-${idx}`}
                    >
                      <div
                        draggable
                        onDragStart={() => setDragPhotoIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          reorderList('photos', dragPhotoIdx, idx);
                          setDragPhotoIdx(null);
                        }}
                        onDragEnd={() => setDragPhotoIdx(null)}
                        className="relative aspect-square cursor-grab active:cursor-grabbing"
                      >
                        <img
                          src={photo.url}
                          alt={photo.alt || `Photo ${idx + 1}`}
                          className="w-full h-full object-cover rounded border border-[#C5A880]/20 pointer-events-none"
                        />
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#1F1A17]/85 text-[#C5A880] text-[10px] font-mono rounded">
                          {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-[#EADBC8] opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove photo"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <input
                        value={photo.caption || ''}
                        onChange={(e) => updatePhotoMeta(idx, 'caption', e.target.value)}
                        placeholder="Caption (optional)"
                        className="w-full mt-1.5 bg-[#1A1612] border border-[#C5A880]/15 rounded px-2 py-1 text-[11px] text-[#EADBC8] placeholder:text-[#EADBC8]/30"
                        data-testid={`photo-caption-${idx}`}
                      />
                      <input
                        value={photo.alt || ''}
                        onChange={(e) => updatePhotoMeta(idx, 'alt', e.target.value)}
                        placeholder="Alt text (accessibility)"
                        className="w-full mt-1 bg-[#1A1612] border border-[#C5A880]/15 rounded px-2 py-1 text-[11px] text-[#EADBC8] placeholder:text-[#EADBC8]/30"
                        data-testid={`photo-alt-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Videos (public) */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">
              Videos ({data.videos?.length || 0})
            </label>
            <p className="text-[#EADBC8]/50 text-xs mb-2">
              Public — viewable by anyone. Great for showreels & highlights.
            </p>
            <input
              ref={videosInputRef}
              type="file"
              accept="video/*"
              multiple
              hidden
              onChange={(e) => handleVideosUpload(e.target.files, 'horizontal')}
            />
            <input
              ref={reelsInputRef}
              type="file"
              accept="video/*"
              multiple
              hidden
              onChange={(e) => handleVideosUpload(e.target.files, 'vertical')}
            />
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => videosInputRef.current?.click()}
                disabled={uploadingVideos}
                className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
                data-testid="upload-videos-btn"
              >
                <Upload size={16} className="mr-2" />
                {uploadingVideos ? 'Uploading…' : 'Add Videos (16:9)'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => reelsInputRef.current?.click()}
                disabled={uploadingVideos}
                className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
                data-testid="upload-reels-btn"
              >
                <Upload size={16} className="mr-2" />
                Add Reels (9:16)
              </Button>
            </div>

            {data.videos?.length > 0 && (
              <>
                <p className="text-[#EADBC8]/50 text-xs mb-2">
                  Drag a video to reorder · top-left first appears on the public site
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {data.videos.map((video, idx) => (
                    <div
                      key={video.public_id || idx}
                      draggable
                      onDragStart={(e) => {
                        // only start drag from the handle area; allow click events on buttons/inputs
                        if (e.target.closest('button,input')) {
                          e.preventDefault();
                          return;
                        }
                        setDragVideoIdx(idx);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        reorderList('videos', dragVideoIdx, idx);
                        setDragVideoIdx(null);
                      }}
                      onDragEnd={() => setDragVideoIdx(null)}
                      className={`bg-[#1A1612] rounded border border-[#C5A880]/20 group overflow-hidden transition-opacity ${
                        dragVideoIdx === idx ? 'opacity-30' : 'opacity-100'
                      }`}
                    >
                      <div className="relative aspect-video cursor-grab active:cursor-grabbing">
                        <img
                          src={getVideoThumbnail(video)}
                          alt={video.title || `Video ${idx + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover pointer-events-none"
                          onError={(e) => {
                            e.currentTarget.style.visibility = 'hidden';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1F1A17]/80 via-transparent to-transparent pointer-events-none" />
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#1F1A17]/85 text-[#C5A880] text-[10px] font-mono rounded">
                          #{idx + 1}{video.orientation === 'vertical' ? ' · Reel' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleVideoFeatured(idx)}
                          className={`absolute top-2 right-12 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                            video.featured
                              ? 'bg-[#C5A880] text-[#1F1A17]'
                              : 'bg-[#1F1A17]/85 text-[#C5A880]/70 hover:text-[#C5A880] border border-[#C5A880]/30'
                          }`}
                          data-testid={`feature-video-${idx}`}
                          aria-label={video.featured ? 'Unfeature video' : 'Set as featured video'}
                          title={video.featured ? 'Featured · click to unset' : 'Mark as featured'}
                        >
                          <Star size={14} fill={video.featured ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeVideo(idx)}
                          className="absolute top-2 right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-[#EADBC8] opacity-80 hover:opacity-100 transition-opacity"
                          data-testid={`remove-video-${idx}`}
                          aria-label="Remove video"
                        >
                          <X size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setThumbPickerIdx(idx)}
                          className="absolute bottom-2 left-2 px-3 py-1.5 bg-[#1F1A17]/85 hover:bg-[#C5A880] hover:text-[#1F1A17] backdrop-blur text-[#C5A880] text-xs rounded flex items-center gap-1.5 transition-colors border border-[#C5A880]/30"
                          data-testid={`pick-thumb-${idx}`}
                        >
                          <ImageIcon size={12} />
                          {video.thumbnail ? 'Change thumbnail' : 'Pick thumbnail'}
                        </button>
                        {video.thumbnail && (
                          <span
                            className="absolute top-2 left-12 px-2 py-0.5 bg-[#C5A880] text-[#1F1A17] text-[10px] font-semibold rounded-full uppercase tracking-wider"
                            data-testid={`custom-thumb-badge-${idx}`}
                          >
                            Custom
                          </span>
                        )}
                      </div>
                      <Input
                        value={video.title || ''}
                        onChange={(e) => {
                          const next = [...(data.videos || [])];
                          next[idx] = { ...next[idx], title: e.target.value };
                          setData((prev) => ({ ...prev, videos: next }));
                        }}
                        placeholder="Video title"
                        className="bg-[#1A1612] border-0 border-t border-[#C5A880]/10 text-[#EADBC8] text-xs rounded-none focus-visible:ring-0"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            <VideoThumbnailPicker
              open={thumbPickerIdx !== null}
              onOpenChange={(o) => !o && setThumbPickerIdx(null)}
              video={thumbPickerIdx !== null ? data.videos?.[thumbPickerIdx] : null}
              onSave={(thumbnail) => {
                const next = [...(data.videos || [])];
                if (next[thumbPickerIdx]) {
                  next[thumbPickerIdx] = { ...next[thumbPickerIdx], thumbnail };
                  setData((prev) => ({ ...prev, videos: next }));
                }
              }}
            />
          </div>

          {/* YouTube / Vimeo embeds */}
          <div>
            <label className="block text-[#EADBC8] text-sm font-medium mb-2">
              YouTube / Vimeo Embeds ({data.embeds?.length || 0})
            </label>
            <p className="text-[#EADBC8]/50 text-xs mb-2">
              Paste a YouTube or Vimeo link — it renders as a responsive embedded player on the portfolio page.
            </p>
            {(data.embeds || []).map((em, idx) => (
              <div key={idx} className="flex gap-2 mb-2" data-testid={`embed-row-${idx}`}>
                <Input
                  value={em.url}
                  onChange={(e) => updateEmbed(idx, 'url', e.target.value)}
                  placeholder="https://youtu.be/… or https://vimeo.com/…"
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] flex-1"
                  data-testid={`embed-url-${idx}`}
                />
                <Input
                  value={em.title || ''}
                  onChange={(e) => updateEmbed(idx, 'title', e.target.value)}
                  placeholder="Title (optional)"
                  className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] w-40 hidden sm:block"
                />
                <Button type="button" variant="outline" onClick={() => removeEmbed(idx)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 px-3">
                  <X size={14} />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addEmbed}
              className="border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10 w-full mt-1"
              data-testid="add-embed-btn">
              <Plus size={16} className="mr-2" /> Add a YouTube / Vimeo link
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#EADBC8] text-sm font-medium mb-2">Display Order</label>
              <Input
                type="number"
                value={data.order || 0}
                onChange={(e) => update('order', Number(e.target.value))}
                className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2">
                <Checkbox
                  checked={!!data.published}
                  onCheckedChange={(v) => update('published', !!v)}
                  className="border-[#C5A880]/40 data-[state=checked]:bg-[#C5A880] data-[state=checked]:text-[#1F1A17]"
                />
                <span className="text-gray-300 text-sm">Published (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-[#3A332E] border-[#C5A880]/20 text-[#EADBC8]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
            data-testid="save-event-btn"
          >
            {saving ? 'Saving...' : 'Save Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    try {
      const data = await adminApi.events.list();
      setEvents(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    try {
      await adminApi.events.remove(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast({ title: 'Event deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#EADBC8] mb-2">Events</h1>
          <p className="text-gray-400">Manage your public event galleries</p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
          data-testid="add-event-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Event
        </Button>
      </div>

      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : events.length === 0 ? (
        <Card className="bg-[#2A2420] border-[#C5A880]/20">
          <CardContent className="p-12 text-center">
            <ImageIcon className="text-[#C5A880] mx-auto mb-4" size={48} />
            <p className="text-gray-400 mb-4">No events yet. Create your first event gallery.</p>
            <Button
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold"
            >
              <Plus size={18} className="mr-2" />
              Add First Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card
              key={event.id}
              className="bg-[#2A2420] border-[#C5A880]/20 overflow-hidden"
              data-testid={`event-row-${event.id}`}
            >
              <div className="aspect-[4/3] bg-[#1F1A17] relative">
                {event.hero_image ? (
                  <img src={event.hero_image} alt={event.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-700">
                    <ImageIcon size={32} />
                  </div>
                )}
                {!event.published && (
                  <span className="absolute top-2 right-2 bg-yellow-500/90 text-[#1F1A17] text-xs px-2 py-1 rounded font-bold">
                    DRAFT
                  </span>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="text-[#EADBC8] font-bold mb-1 truncate">{event.title}</h3>
                <p className="text-gray-400 text-xs mb-3 truncate">
                  {event.event_type} · {event.photos?.length || 0} photos
                </p>
                <div className="flex items-center gap-2 mb-3 text-xs">
                  <Globe size={12} className="text-[#C5A880]" />
                  <span className="text-[#C5A880]">Public gallery · {(event.photos?.length || 0)} photos</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditing(event);
                      setShowForm(true);
                    }}
                    className="flex-1 border-[#C5A880]/30 text-[#C5A880] hover:bg-[#C5A880]/10"
                    data-testid={`edit-event-${event.id}`}
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#2A2420] border-[#C5A880]/20">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#EADBC8]">Delete this event?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          This will permanently delete the event and all its photos from Cloudinary.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[#3A332E] text-[#EADBC8] border-[#C5A880]/20">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(event.id)}
                          className="bg-red-500 hover:bg-red-600 text-[#EADBC8]"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EventForm
        open={showForm}
        onOpenChange={(o) => {
          setShowForm(o);
          if (!o) setEditing(null);
        }}
        event={editing}
        onSaved={load}
      />
    </div>
  );
};

export default EventsAdmin;
